import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import LinkIcon from "@mui/icons-material/Link";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useEffect, useRef, useState } from "react";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { isSelfSendMode } from "@/utils/campaignMode-helper";

interface Props {
  onEnterFullscreen: () => void;
}

interface MenuItemProps {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
}

function MenuItem({ icon: Icon, label, onClick }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="group flex items-center gap-2.5 w-full px-3 py-2.5 bg-transparent border-none rounded-btn font-lato text-[13px] text-rodeo-black text-left cursor-pointer transition-colors hover:bg-clear-dust"
    >
      <span className="shrink-0 text-graphite group-hover:text-dark-dust transition-colors">
        <Icon sx={{ fontSize: 18 }} />
      </span>
      {label}
    </button>
  );
}

export default function PreviewDesignDropdown({ onEnterFullscreen }: Props) {
  const [open, setOpen] = useState(false);
  const { openSendTest, showToast, mockToken, notificationMode } = useCampaignBuilder();
  const wrapRef = useRef<HTMLDivElement>(null);

  // Self-send (manual + universal): no GfG email goes out, so a "Send Email Test" makes no sense — hide it.
  const emailLocked = isSelfSendMode(notificationMode);

  // Copy the campaign's REAL demo link — its mock/test short URL (isTest, campaign-level), the
  // same token the recipient redemption preview resolves. Absent until the draft exists, so
  // prompt the user to save first rather than minting a throwaway slug.
  function copyDemoLink() {
    if (!mockToken) {
      showToast("Save your campaign first to get its demo link");
      return;
    }
    navigator.clipboard
      .writeText(`${window.location.origin}/g/${mockToken}`)
      .then(() => showToast("Demo link copied to clipboard"))
      .catch(() => {});
  }

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        id="tour-preview-btn"
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={[
          "inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-btn border font-lato text-[12px] font-semibold transition-all",
          open
            ? "border-dark-dust text-dark-dust bg-white"
            : "border-smoke text-graphite bg-white hover:border-dark-dust hover:text-dark-dust",
        ].join(" ")}
      >
        <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
        Preview
        <ExpandMoreIcon
          sx={{
            fontSize: 16,
            ml: 0.25,
            transition: "transform 0.18s ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute top-[calc(100%+6px)] right-0 min-w-60 bg-white rounded-card flex flex-col p-1.5 z-250"
          style={{
            border: "1px solid var(--color-card-border)",
            boxShadow: "0 8px 24px rgba(26,22,18,0.12)",
            gap: 2,
          }}
        >
          {!emailLocked && (
            <MenuItem
              icon={SendOutlinedIcon}
              label="Send Email Test"
              onClick={() => {
                setOpen(false);
                openSendTest();
              }}
            />
          )}
          <MenuItem
            icon={FullscreenIcon}
            label="Enter Full Screen Preview"
            onClick={() => {
              setOpen(false);
              onEnterFullscreen();
            }}
          />
          <MenuItem
            icon={LinkIcon}
            label="Get a demo link"
            onClick={() => {
              setOpen(false);
              copyDemoLink();
            }}
          />
        </div>
      )}
    </div>
  );
}
